import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, Typography, Stack } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useTranslation } from 'react-i18next';
import { apiClient, handleApiError } from '../../../services/apiClient';
import { buildApiUrl, API_ENDPOINTS } from '../../../config/apiEndpoints';

interface Manager {
  user_id: number;
  avgAcceptedPricePerKm: number | null;
  name?: string;
  role?: string;
}

interface TopPerformingManagersProps {
  days?: number;
}

const TopPerformingManagers: React.FC<TopPerformingManagersProps> = ({ days = 7 }) => {
  const { t } = useTranslation();
  const [managers, setManagers] = useState<Manager[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      try {
        const url = buildApiUrl(API_ENDPOINTS.stats.users);
        const params = new URLSearchParams();
        params.append('days', days.toString());
        const response = await apiClient(url + '?' + params, { method: 'GET' });
        const data = await response.json();

        if (data.result?.avgAcceptedPricesPerKm) {
          // Sort by avgAcceptedPricePerKm descending and take top 5
          const sorted = data.result.avgAcceptedPricesPerKm
            .sort((a: Manager, b: Manager) => (b.avgAcceptedPricePerKm || 0) - (a.avgAcceptedPricePerKm || 0))
            .slice(0, 5);
          setManagers(sorted);
        }
      } catch (error) {
        handleApiError(error, t('Failed to fetch manager stats'));
        setManagers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [days, t]);

  return (
    <Card
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: { xs: 2, sm: 3 },
        border: (theme: any) => `1px solid ${theme.palette.border.default}`,
        bgcolor: (theme: any) => theme.palette.mode === 'dark' ? theme.palette.background.white : 'transparent',
        overflow: 'hidden',
        boxShadow: 'none',
      }}
    >
      <CardContent sx={{
        p: 0,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        '&.MuiCardContent-root': {
          backgroundColor: (theme: any) => theme.palette.mode === 'dark' ? theme.palette.background.white : 'transparent',
        },
      }}>
        <Box
          sx={{
            px: { xs: 1.5, sm: 2, md: 2 },
            py: { xs: 1, sm: 1.25, md: 1.5 },
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography
            sx={{
              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.125rem' },
              fontWeight: 600,
              color: (theme: any) => theme.palette.mode === 'dark' ? theme.palette.text.white : theme.palette.text.dark,
            }}
          >
            {t('Top Performing Managers')}
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              color: 'text.disabled',
              cursor: 'not-allowed',
              opacity: 0.5,
            }}
          >
            {t('See All')}
          </Typography>
        </Box>

        <Box sx={{ px: { xs: 1.5, sm: 2, md: 2 }, py: { xs: 1, sm: 1.25 }, flex: 1 }}>
          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
              <Typography sx={{ color: 'text.grey' }}>{t('Loading...')}</Typography>
            </Box>
          ) : managers.length === 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
              <Typography sx={{ color: 'text.grey' }}>{t('No data available')}</Typography>
            </Box>
          ) : (
            <Stack spacing={0}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1fr 1fr',
                  gap: { xs: 1, sm: 2 },
                  pb: { xs: 0.75, sm: 1 },
                  borderBottom: (theme: any) => `1px solid ${theme.palette.border.light}`,
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.81rem' },
                    fontWeight: 600,
                    color: 'text.grey',
                    textTransform: 'uppercase',
                  }}
                >
                  {t('User ID')}
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.81rem' },
                    fontWeight: 600,
                    color: 'text.grey',
                    textTransform: 'uppercase',
                    textAlign: 'right',
                  }}
                >
                  {t('Avg Price')}
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.81rem' },
                    fontWeight: 600,
                    color: 'text.grey',
                    textTransform: 'uppercase',
                    textAlign: 'right',
                  }}
                >
                  {t('Per KM')}
                </Typography>
              </Box>

              {managers.map((manager, index) => (
                <Box
                  key={`${manager.user_id}-${index}`}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '2fr 1fr 1fr',
                    gap: { xs: 1, sm: 2 },
                    py: { xs: 0.875, sm: 1, md: 1.125 },
                    borderBottom: (theme: any) => `1px solid ${theme.palette.border.light}`,
                    transition: 'background-color 0.2s ease',
                    '&:last-child': {
                      borderBottom: 'none',
                    },
                    '&:hover': {
                      backgroundColor: (theme: any) => theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.05)'
                        : 'rgba(0, 0, 0, 0.03)',
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: { xs: '0.75rem', sm: '0.81rem', md: '0.875rem' },
                      fontWeight: 500,
                      color: (theme: any) => theme.palette.mode === 'dark' ? theme.palette.text.white : theme.palette.text.dark,
                      alignSelf: 'center',
                    }}
                  >
                    {manager.user_id}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: '0.75rem', sm: '0.81rem', md: '0.875rem' },
                      fontWeight: 500,
                      color: (theme: any) => theme.palette.mode === 'dark' ? theme.palette.text.white : theme.palette.text.dark,
                      textAlign: 'right',
                      alignSelf: 'center',
                    }}
                  >
                    €{manager.avgAcceptedPricePerKm?.toFixed(2) || 'N/A'}
                  </Typography>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="flex-end"
                    spacing={0.5}
                    sx={{ alignSelf: 'center' }}
                  >
                    <TrendingUpIcon
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                        color: '#66bb6a',
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: { xs: '0.75rem', sm: '0.81rem', md: '0.875rem' },
                        fontWeight: 500,
                        color: '#66bb6a',
                        textAlign: 'right',
                      }}
                    >
                      {(manager.avgAcceptedPricePerKm || 0).toFixed(2)}
                    </Typography>
                  </Stack>
                </Box>
              ))}
            </Stack>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default TopPerformingManagers;

